import { PureInput, InputProps, PureInputType } from './PureInput';
import { commify, sanitizer } from '@services/MarppleShop/shared/util/commify.ts';

export interface NumberTextInputType extends PureInputType {
  getNumericValue(): number | null;
}

export interface NumberTextInputProps extends Omit<InputProps, 'type' | 'value' | 'onFocusout'> {
  value?: number | null;
  min?: number;
  max?: number;
  onFocusout?: (view: NumberTextInputType) => void;
  is_commify?: boolean;
  decimal?: number;
}

export class NumberTextInput extends PureInput implements NumberTextInputType {
  private min: number;
  private max?: number;
  private decimal?: number;
  private is_commify: boolean;

  constructor(data: NumberTextInputProps) {
    const originalOnInput = data.onInput;
    const originalOnFocusout = data.onFocusout;

    const { rows, is_commify = true, ...pureInputData } = data;

    // PureInput의 생성자로 전달할 props 구성
    const inputProps: InputProps = {
      ...pureInputData,
      type: 'text',
      value:
        data.value === null || data.value === undefined
          ? ''
          : is_commify
            ? commify(data.value.toString())
            : data.value.toString(),
      onInput: (view) => {
        // 숫자 입력 처리 로직
        this.handleNumberTextInput(view);

        // 기존 onInput이 있으면 호출
        if (originalOnInput) {
          originalOnInput(view);
        }
      },
      onFocusout: (view) => {
        this.handleFocusout(view);

        if (originalOnFocusout) {
          originalOnFocusout(this);
        }
      },
    };

    super(inputProps);

    this.min = data.min !== undefined ? data.min : Number.MIN_SAFE_INTEGER;
    this.max = data.max;
    this.decimal = data.decimal;
    this.is_commify = Boolean(is_commify);
  }

  private commify(value: string) {
    return this.is_commify ? commify(value) : value;
  }

  private handleNumberTextInput(view: PureInputType): void {
    const inputValue = view.getValue();
    const numericValue = sanitizer.number(inputValue, this.decimal);

    // 소수점으로 끝나는 경우 그대로 유지 (포커스아웃에서 처리)
    if (inputValue.endsWith('.')) {
      return;
    }

    // 빈 문자열 체크
    if (numericValue === '') {
      view.setValue('');
      return;
    }

    // 빅인트 체크
    if (!this.max) {
      try {
        const bigInt = BigInt(numericValue);
        if (bigInt > Number.MAX_SAFE_INTEGER) {
          view.setValue(bigInt);
          return;
        }
      } catch (_) {}
    }

    let numValue = this.decimal ? parseFloat(numericValue) : parseInt(numericValue, 10);

    // NaN 체크
    if (isNaN(numValue)) {
      view.setValue('');
      return;
    }

    // 범위 체크
    if (numValue < this.min) {
      numValue = this.min;
    } else if (this.max && numValue > this.max) {
      numValue = this.max;
    }

    const formattedValue = this.commify(numValue.toString());
    // 값이 변경되었으면 업데이트
    if (formattedValue !== inputValue) {
      view.setValue(formattedValue);
    }
  }

  private handleFocusout(view: PureInputType): void {
    const value = view.getValue();

    // 빈 값이고 placeholder가 없는 경우에만 기본값 설정
    if ((value === '' || sanitizer.number(value, this.decimal) === '') && !this.data.placeholder) {
      let defaultValue = 0;

      // 0이 min보다 작으면 min 값 사용
      if (defaultValue < this.min) {
        defaultValue = this.min;
      }

      view.setValue(this.commify(defaultValue.toString()));
    }

    if (value.endsWith('.')) {
      view.setValue(this.commify(parseFloat(value).toString()));
    }
  }

  setMax(max: number) {
    this.max = max;
  }

  setMin(min: number) {
    this.min = min;
  }

  getNumericValue(): number | null {
    const value = this.getValue();
    if (value === '') return null;

    const numericValue = sanitizer.number(value, this.decimal);
    if (numericValue === '') return null;

    const numValue = this.decimal ? parseFloat(numericValue) : parseInt(numericValue, 10);
    return isNaN(numValue) ? null : numValue;
  }

  override setValue(value: number | bigint | string | null): this {
    if (value === null || value === undefined) {
      super.setValue('');
      return this;
    }

    let numValue: number;

    if (typeof value === 'string') {
      const numericValue = sanitizer.number(value, this.decimal);
      if (numericValue === '') {
        super.setValue('');
        return this;
      }
      numValue = this.decimal ? parseFloat(numericValue) : parseInt(numericValue, 10);
      if (isNaN(numValue)) {
        super.setValue('');
        return this;
      }
    } else if (typeof value === 'bigint') {
      if (!this.max && value > Number.MAX_SAFE_INTEGER) {
        super.setValue(this.commify(value.toString()));
      }
      return this;
    } else {
      numValue = value;
    }

    if (numValue < this.min) {
      numValue = this.min;
    } else if (this.max && numValue > this.max) {
      numValue = this.max;
    }

    super.setValue(this.commify(numValue.toString()));
    return this;
  }
}
